import { useTranslation } from 'react-i18next';
import { Check, Clock } from 'lucide-react';
import { PrefSection, type PrefVariant } from '@/components/PrefSection';
import { useTimeFormat, type TimeFormat } from '@/hooks/useTimeFormat';
import { cn } from '@/lib/utils';

/**
 * Profile-page control for the 12-hour vs 24-hour clock. Applies wherever times
 * of day are shown (food log, timeline, sleep, meal detail). Persists to this
 * browser like the other display prefs; takes effect on the next view render.
 */
const OPTIONS: Array<{ value: TimeFormat; label: string; example: string }> = [
  { value: '12h', label: '12-hour', example: '8:24 PM' },
  { value: '24h', label: '24-hour', example: '20:24' },
];

export const TimeFormatSwitcher = ({ variant = 'card' }: { variant?: PrefVariant }) => {
  const { t } = useTranslation();
  const [format, setFormat] = useTimeFormat();

  return (
    <PrefSection
      variant={variant}
      icon={Clock}
      title={t('Time format')}
      description={t('How times of day are shown across the portal. Saved to this browser.')}
    >
      <div className="grid gap-2 sm:grid-cols-2">
        {OPTIONS.map((o) => {
          const active = format === o.value;
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => setFormat(o.value)}
              aria-pressed={active}
              className={cn(
                'flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm transition-colors',
                active ? 'border-primary bg-primary/5' : 'border-border bg-surface hover:bg-muted',
              )}
            >
              <span className="flex items-center gap-1.5 font-medium">
                {t(o.label)}
                {active && <Check className="h-3.5 w-3.5 text-primary" />}
              </span>
              <span className="tabular-nums text-xs text-muted-foreground">{o.example}</span>
            </button>
          );
        })}
      </div>
    </PrefSection>
  );
};
